import type { RewardPool } from '@shared/api/types/rewardPools';
import { REWARD_POOL_STATUS } from '@shared/api/types/rewardPools';

import { useSelfEntriesAmountByCurrency } from '@store:web/components/entities/rewardPool/model/useSelfEntriesAmountByCurrency';

import { useRequirements } from '../../model/useRequirements';
import { GetTicketsButton } from './buttons/GetTicketsButton/GetTicketsButton';
import { ViewTicketsButton } from './buttons/ViewTicketsButton/ViewTicketsButton';
import { List } from './List/List';
import { ListItem } from './List/ListItem';
import { ListWrapper } from './List/ListWrapper';

type Props = {
  rewardPool: RewardPool;
  hasBorders?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
};

export const EntryOptionsList = ({
  rewardPool,
  showFooter = true,
  showHeader = true,
  hasBorders = true,
}: Props) => {
  const { requirements, hasRequirements, query } = useRequirements(rewardPool);
  const isPoolCompleted = rewardPool.status === REWARD_POOL_STATUS.COMPLETED;
  const { entriesAmountByCurrency } = useSelfEntriesAmountByCurrency({ poolId: rewardPool.id });

  return (
    <ListWrapper
      showFooter={showFooter}
      header={showHeader && <h2 className="heading-h2-adaptive">Raffle Entry Options</h2>}
    >
      <List
        isEmpty={!hasRequirements}
        query={query}
        hasBorders={hasBorders}
        data-qa="requirements_list"
      >
        {requirements.map(({ id, currencyType, title, description, action }) => {
          const entryAmount = currencyType?.currency
            ? entriesAmountByCurrency[currencyType.currency]
            : 0;
          return (
            <ListItem
              key={id}
              data-qa={`requirement-id_${id}`}
              isDisabled={isPoolCompleted}
              label={title}
              description={description}
              buttons={
                <>
                  <ViewTicketsButton
                    rewardPool={rewardPool}
                    currency={currencyType?.currency}
                    invisible={!entryAmount}
                    dataQaAttribute="view-my-tickets-button"
                  >
                    View Tickets
                  </ViewTicketsButton>
                  {!isPoolCompleted && currencyType && (
                    <GetTicketsButton
                      rewardPool={rewardPool}
                      currencyType={currencyType}
                      dataQaAttribute={`get-ticket-for-${currencyType.currency}`}
                    >
                      {entryAmount > 0 ? 'Get More' : action?.label}
                    </GetTicketsButton>
                  )}
                </>
              }
              counter={entryAmount}
            />
          );
        })}
      </List>
    </ListWrapper>
  );
};
